import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Trophy, Copy, Check, Loader2, User } from "lucide-react";

interface Winner {
  id: string;
  picked_at: string;
  participant_id: string;
  meta: {
    winning_entry_id?: string;
    total_entries_at_pick?: number;
    participant_entries?: number;
  } | null;
  participant: {
    id: string;
    email: string | null;
    referral_code: string;
  } | null;
}

interface WinnersHistoryProps {
  contestId: string;
}

export function WinnersHistory({ contestId }: WinnersHistoryProps) {
  const { toast } = useToast();
  const [copiedId, setCopiedId] = useState<string | null>(null);

  const { data: winners, isLoading } = useQuery({
    queryKey: ["winners", contestId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("winners")
        .select(`
          id,
          picked_at,
          participant_id,
          meta,
          participants!winners_participant_id_fkey (
            id,
            email,
            referral_code
          )
        `)
        .eq("contest_id", contestId)
        .order("picked_at", { ascending: false });

      if (error) throw error;

      // Transform the data to match our interface
      return (data || []).map((w) => ({
        id: w.id,
        picked_at: w.picked_at,
        participant_id: w.participant_id,
        meta: w.meta as Winner["meta"],
        participant: w.participants as Winner["participant"],
      }));
    },
  });

  const copyEmail = async (email: string, winnerId: string) => {
    await navigator.clipboard.writeText(email);
    setCopiedId(winnerId);
    toast({
      title: "Email Copied",
      description: "Winner email copied to clipboard",
    });
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!winners?.length) {
    return (
      <div className="text-center py-8">
        <Trophy className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
        <p className="text-muted-foreground">No winners picked yet</p>
        <p className="text-sm text-muted-foreground mt-1">
          Use the "Pick Winner" button to select a random winner
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {winners.map((winner) => (
        <div
          key={winner.id}
          className="bg-gradient-card rounded-lg border border-border p-4 space-y-2"
        >
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-success" />
              <span className="text-sm text-muted-foreground">
                {format(new Date(winner.picked_at), "MMM d, yyyy 'at' h:mm a")}
              </span>
            </div>
            {winner.participant?.email && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => copyEmail(winner.participant!.email!, winner.id)}
              >
                {copiedId === winner.id ? (
                  <>
                    <Check className="w-4 h-4 mr-1" />
                    Copied
                  </>
                ) : (
                  <>
                    <Copy className="w-4 h-4 mr-1" />
                    Copy Email
                  </>
                )}
              </Button>
            )}
          </div>

          <div className="space-y-1">
            {winner.participant?.email ? (
              <p className="font-medium">{winner.participant.email}</p>
            ) : (
              <div className="flex items-center gap-2 text-muted-foreground">
                <User className="w-4 h-4" />
                <span className="font-mono text-sm">{winner.participant_id}</span>
              </div>
            )}

            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-muted-foreground">
              {winner.meta?.participant_entries !== undefined && (
                <span>
                  Entries: <strong className="text-foreground">{winner.meta.participant_entries}</strong>
                </span>
              )}
              {winner.participant?.referral_code && (
                <span>
                  Referral: <code className="text-xs">{winner.participant.referral_code}</code>
                </span>
              )}
              {winner.meta?.winning_entry_id && (
                <span>
                  Entry ID: <code className="text-xs">{winner.meta.winning_entry_id.slice(0, 8)}...</code>
                </span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
